import { createFileRoute } from '@tanstack/react-router';
import { Suspense, useState } from 'react';
import { Download, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import type { BulkUploadState } from '@repo/types';
import {
  FileUploadDropzone,
  ParsingStatus,
  ValidationSummary,
  ValidRowsTable,
  InvalidRowsTable,
  DuplicatesTable,
  ImportActions,
  ImportProgress,
} from '@/components/bulk-upload';
import { useParseBulkUpload, useImportBulkUpload, useDownloadTemplate } from '@/hooks/useJobs';

export const Route = createFileRoute('/admin/jobs/bulk-upload')({
  component: BulkUploadPage,
});

function BulkUploadPage() {
  const [uploadState, setUploadState] = useState<BulkUploadState>({
    uploadedFile: null,
    parseResults: null,
    importStatus: 'idle',
    importedJobs: [],
    errors: [],
  });

  const [importProgress, setImportProgress] = useState(0);

  // API hooks
  const parseBulkUploadMutation = useParseBulkUpload();
  const importBulkUploadMutation = useImportBulkUpload();
  const downloadTemplateMutation = useDownloadTemplate();

  const handleFile = async (file: File) => {
    // Validate file type
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.csv')) {
      toast.error('Invalid file type. Please upload a CSV file.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setUploadState((prev) => ({
      ...prev,
      uploadedFile: file,
      importStatus: 'parsing',
    }));

    // Parse CSV file using real API
    try {
      const results = await parseBulkUploadMutation.mutateAsync(file);

      setUploadState((prev) => ({
        ...prev,
        parseResults: results,
        importStatus: 'validating',
      }));

      toast.success(`Parsed ${results.totalRows} rows successfully`);
    } catch (error: any) {
      setUploadState((prev) => ({
        ...prev,
        importStatus: 'failed',
        errors: [error.message || 'Failed to parse CSV file. Please check the format.'],
      }));
      toast.error('Failed to parse file');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadTemplateMutation.mutateAsync();
      toast.success('Template downloaded');
    } catch (error: any) {
      toast.error(error.message || 'Failed to download template');
    }
  };

  const handleImport = async () => {
    if (!uploadState.parseResults) return;

    setUploadState((prev) => ({
      ...prev,
      importStatus: 'importing',
    }));
    setImportProgress(0);

    try {
      const validRows = uploadState.parseResults.validRows.map((row) => row.data);

      // Progress simulation (real API doesn't return progress)
      const interval = setInterval(() => {
        setImportProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const result = await importBulkUploadMutation.mutateAsync(validRows as any);

      clearInterval(interval);
      setImportProgress(100);

      setUploadState((prev) => ({
        ...prev,
        importStatus: 'completed',
        importedJobs: result.jobIds || [],
      }));

      toast.success(`Successfully imported ${result.imported} jobs${result.failed > 0 ? `, ${result.failed} failed` : ''}`);
    } catch (error: any) {
      console.error(error);
      setUploadState((prev) => ({
        ...prev,
        importStatus: 'failed',
        errors: [error.message || 'Import failed. Please try again.'],
      }));
      toast.error('Import failed');
    }
  };

  const handleReset = () => {
    setUploadState({
      uploadedFile: null,
      parseResults: null,
      importStatus: 'idle',
      importedJobs: [],
      errors: [],
    });
    setImportProgress(0);
  };

  const { parseResults, importStatus, importedJobs, errors } = uploadState;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Bulk Upload Jobs</h1>
          <p className="text-sm text-muted-foreground">
            Import multiple jobs via CSV file upload
          </p>
        </div>
      </div>

       <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
          <Download className="mr-2 h-4 w-4" />
          Download Template
      </Button>

      {/* Upload Section */}
      {importStatus === 'idle' && (
        <Suspense fallback={<Skeleton className="h-64 rounded-lg" />}>
          <FileUploadDropzone onFileSelect={handleFile} />
        </Suspense>
      )}

      {/* Parsing Status */}
      {importStatus === 'parsing' && (
        <Suspense fallback={<Skeleton className="h-48 rounded-lg" />}>
          <ParsingStatus />
        </Suspense>
      )}

      {/* Validation Results */}
      {parseResults && (importStatus === 'validating' || importStatus === 'importing' || importStatus === 'completed') && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <Suspense fallback={<Skeleton className="h-24 rounded-lg" />}>
            <ValidationSummary results={parseResults} />
          </Suspense>
          {/* Valid Rows */}
          {parseResults.validRows.length > 0 && (
            <Suspense fallback={<Skeleton className="h-96 rounded-lg" />}>
              <ValidRowsTable rows={parseResults.validRows} />
            </Suspense>
          )}



          {/* Invalid Rows */}
          {parseResults.invalidRows.length > 0 && (
            <Suspense fallback={<Skeleton className="h-96 rounded-lg" />}>
              <InvalidRowsTable rows={parseResults.invalidRows} />
            </Suspense>
          )}

          {/* Duplicate Warnings */}
          {parseResults.duplicates.length > 0 && (
            <Suspense fallback={<Skeleton className="h-96 rounded-lg" />}>
              <DuplicatesTable rows={parseResults.duplicates} />
            </Suspense>
          )}

          {/* Import Progress */}
          {importStatus === 'importing' && (
            <Suspense fallback={<Skeleton className="h-48 rounded-lg" />}>
              <ImportProgress
                current={Math.floor((importProgress / 100) * parseResults.validRows.length)}
                total={parseResults.validRows.length}
                isComplete={false}
              />
            </Suspense>
          )}

          {/* Import Success */}
          {importStatus === 'completed' && (
            <Alert className="border-green-600 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-900">
                Successfully imported {importedJobs.length} jobs! You can view them in the All Jobs page.
              </AlertDescription>
            </Alert>
          )}

          {/* Actions */}
          {importStatus === 'validating' && (
            <Suspense fallback={<Skeleton className="h-10 w-full rounded-lg" />}>
              <ImportActions
                onStartOver={handleReset}
                onImport={handleImport}
                validCount={parseResults.validRows.length}
                isImporting={false}
              />
            </Suspense>
          )}
          {importStatus === 'completed' && (
            <Suspense fallback={<Skeleton className="h-10 w-32 rounded-lg" />}>
              <Button variant="outline" onClick={handleReset}>
                Start Over
              </Button>
            </Suspense>
          )}
        </div>
      )}

      {/* Error State */}
      {importStatus === 'failed' && errors.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-inside list-disc">
                  {errors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
            <div className="mt-4">
              <Button variant="outline" onClick={handleReset}>
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
